import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import pluralize from "@/lib/pluralize";

export type FinalizeElectionsDialogProps = {
  open: boolean;
  selectedCount: number;
  missingClassCount?: number;
  cannotFinalize?: boolean;
  confirming?: boolean;
  onClose(): void;
  onConfirm(): void;
};

function classWarningCopy(cannotFinalize: boolean, missingClassCount: number): string | null {
  if (cannotFinalize) {
    return "None of the selected people have a member A-class (for example A-1). Add a member job classification before finalizing.";
  }
  if (missingClassCount > 0) {
    return `${missingClassCount} of the selected people have no member A-class. Those elections will fail; people with a class can still be elected.`;
  }
  return null;
}

export default function FinalizeElectionsDialog({
  open,
  selectedCount,
  missingClassCount = 0,
  cannotFinalize = false,
  confirming = false,
  onClose,
  onConfirm
}: FinalizeElectionsDialogProps) {
  const personLabel = pluralize(selectedCount, "person", undefined, "people");
  const personLabelTitle = pluralize(selectedCount, "Person", undefined, "People");
  const classWarning = classWarningCopy(cannotFinalize, missingClassCount);

  return (
    <Dialog
      open={open}
      headline="Finalize Elections"
      onClosed={onClose}
      disableClose={confirming}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={confirming} testId="finalize-elections-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={onConfirm}
            disabled={selectedCount === 0 || confirming || cannotFinalize}
            testId="finalize-elections-confirm"
          >
            Finalize {selectedCount} {personLabelTitle}
          </Button>
        </>
      }
    >
      <div>
        <p className="text-body-medium-semibold mb-4" data-testid="finalize-elections-count">
          Total People to Finalize Elections: <strong>{selectedCount}</strong>
        </p>
        <p className="text-body-medium">
          Are you sure you want to finalize the election of {selectedCount} selected {personLabel}? This will process
          their {pluralize(selectedCount, "membership election")} immediately. A draft Member dues order will be created
          for each successful election and will remain in draft.
        </p>
        {classWarning ? (
          <div className="mt-4">
            <Banner
              variant="warn"
              description={classWarning}
              hideSecondaryDismiss
              testId="finalize-elections-class-warning"
            />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
